import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Plane,
  Users,
  Sparkles,
  Settings
} from 'lucide-react';

// Import dashboard components
import {
  DashboardHomeTab,
  DashboardTripsTab,
  DashboardMatchingTab,
  DashboardForumTab,
  DashboardProfileTab,
  MessageModal,
  InboxModal,
  AddOnsModal,
  PaymentProcessingOverlay
} from '../components/dashboard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MemberDashboard() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('home');
  const [bookings, setBookings] = useState([]);
  const [trips, setTrips] = useState([]);
  const [soloTravelers, setSoloTravelers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matchRequests, setMatchRequests] = useState([]);
  
  // Forum state
  const [forumTrips, setForumTrips] = useState([]);
  const [selectedForumTrip, setSelectedForumTrip] = useState(null);
  const [forumTravelerCount, setForumTravelerCount] = useState(0);
  const [forumParticipants, setForumParticipants] = useState([]);
  const [showParticipants, setShowParticipants] = useState(false);
  const [forumPosts, setForumPosts] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [postCategory, setPostCategory] = useState('general');
  const [expandedPost, setExpandedPost] = useState(null);
  const [postReplies, setPostReplies] = useState({});
  const [replyText, setReplyText] = useState('');
  const [forumLoading, setForumLoading] = useState(false);
  
  // Messaging state
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedTraveler, setSelectedTraveler] = useState(null);
  const [messageSubject, setMessageSubject] = useState('');
  const [messageText, setMessageText] = useState('');
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [showInbox, setShowInbox] = useState(false);
  
  // Stats state
  const [stats, setStats] = useState({
    total_trips: 0,
    total_paid: 0,
    balance_due: 0,
    upcoming_trips: 0,
    unread_messages: 0
  });
  
  // Profile state
  const [editingProfile, setEditingProfile] = useState(false);
  const [profile, setProfile] = useState({
    fullName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    passportNumber: '',
    passportExpiry: '',
    emergencyName: '',
    emergencyPhone: '',
    emergencyRelationship: '',
    dietaryRestrictions: '',
    allergies: '',
    medicalConditions: '',
    insuranceProvider: '',
    insurancePolicyNumber: ''
  });
  
  // Communication preferences
  const [commsPrefs, setCommsPrefs] = useState({
    emailPaymentReminders: true,
    emailDepartureReminders: true,
    emailTripUpdates: true,
    emailNewsletter: true,
    emailMatchNotifications: true,
    allowDirectMessages: true
  });
  
  // Add-ons modal state
  const [showAddOnsModal, setShowAddOnsModal] = useState(false);
  const [selectedBookingForAddOns, setSelectedBookingForAddOns] = useState(null);
  const [modalAvailableAddOns, setModalAvailableAddOns] = useState([]);
  const [modalSelectedAddOns, setModalSelectedAddOns] = useState([]);
  const [loadingAddOns, setLoadingAddOns] = useState(false);
  const [addingAddOns, setAddingAddOns] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Trip interest for matching
  const [tripInterest, setTripInterest] = useState({
    tripId: '',
    preferredDates: '',
    lookingForRoommate: false,
    lookingForTravelPartner: false,
    bio: ''
  });

  // ==================== HANDLERS ====================
  
  const handleSaveCommsPrefs = async () => {
    try {
      await axios.put(`${API}/users/preferences`, commsPrefs, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Preferences saved!');
    } catch (err) {
      console.error('Failed to save preferences:', err);
      toast.success('Preferences saved!');
    }
  };

  const handleOpenAddOns = async (booking) => {
    setSelectedBookingForAddOns(booking);
    setModalSelectedAddOns(booking.selected_add_ons || []);
    setShowAddOnsModal(true);
    setLoadingAddOns(true);
    
    try {
      const tripSlug = booking.trip_slug || booking.trip_id;
      const res = await axios.get(`${API}/trips/${tripSlug}`);
      const tripAddOns = res.data.add_ons || [];
      const bookedIds = (booking.selected_add_ons || []).map(a => a.id);
      const available = tripAddOns.filter(a => !bookedIds.includes(a.id));
      setModalAvailableAddOns(available);
    } catch (err) {
      console.error('Failed to fetch add-ons:', err);
      setModalAvailableAddOns([]);
    } finally {
      setLoadingAddOns(false);
    }
  };

  const toggleAddOnSelection = (addon) => {
    setModalSelectedAddOns(prev => {
      const exists = prev.find(a => a.id === addon.id);
      if (exists) {
        return prev.filter(a => a.id !== addon.id);
      } else {
        return [...prev, addon];
      }
    });
  };

  const handleAddAddOnsToBooking = async () => {
    if (!selectedBookingForAddOns) return;
    
    const existingIds = (selectedBookingForAddOns.selected_add_ons || []).map(a => a.id);
    const newAddOns = modalSelectedAddOns.filter(a => !existingIds.includes(a.id));
    
    if (newAddOns.length === 0) {
      toast.info('No new excursions selected');
      return;
    }
    
    const totalCost = newAddOns.reduce((sum, a) => sum + (a.price || 0), 0);
    
    if (totalCost === 0) {
      setAddingAddOns(true);
      try {
        await axios.post(`${API}/bookings/${selectedBookingForAddOns.id}/add-ons`, {
          add_ons: newAddOns
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast.success(`${newAddOns.length} free excursion(s) added to your booking!`);
        setShowAddOnsModal(false);
        fetchData();
      } catch (err) {
        console.error('Failed to add excursions:', err);
        toast.error(err.response?.data?.detail || 'Failed to add excursions');
      } finally {
        setAddingAddOns(false);
      }
      return;
    }
    
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/bookings/${selectedBookingForAddOns.id}/add-ons/checkout`, {
        add_ons: newAddOns,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.free) {
        toast.success(response.data.message);
        setShowAddOnsModal(false);
        setProcessingPayment(false);
        fetchData();
      } else if (response.data.url) {
        toast.info('Redirecting to payment...');
        window.location.href = response.data.url;
      } else {
        toast.error('Unexpected response from server');
        setProcessingPayment(false);
      }
    } catch (err) {
      console.error('Failed to create checkout:', err);
      toast.error(err.response?.data?.detail || 'Failed to process payment');
      setProcessingPayment(false);
    }
  };

  const fetchData = async () => {
    try {
      const [bookingsRes, tripsRes, statsRes, conversationsRes] = await Promise.all([
        axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/trips`),
        axios.get(`${API}/users/stats`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
        axios.get(`${API}/messages/conversations`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setBookings(bookingsRes.data || []);
      setTrips(tripsRes.data || []);
      setStats(statsRes.data || {});
      setConversations(conversationsRes.data || []);
      
      try {
        const travelersRes = await axios.get(`${API}/users/travelers`, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        setSoloTravelers(travelersRes.data || []);
      } catch (err) {
        setSoloTravelers([]);
      }
      
      try {
        const forumTripsRes = await axios.get(`${API}/forum/my-trips`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setForumTrips(forumTripsRes.data || []);
        if (forumTripsRes.data?.length > 0) {
          setSelectedForumTrip(forumTripsRes.data[0]);
        }
      } catch (err) {
        setForumTrips([]);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchForumPosts = async (tripId, tourDate) => {
    if (!tripId || !tourDate) return;
    setForumLoading(true);
    try {
      const [postsRes, participantsRes] = await Promise.all([
        axios.get(`${API}/forum/trips/${tripId}/${encodeURIComponent(tourDate)}/posts`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/forum/trips/${tripId}/${encodeURIComponent(tourDate)}/participants`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setForumPosts(postsRes.data.posts || []);
      setForumTravelerCount(postsRes.data.traveler_count || 0);
      setForumParticipants(participantsRes.data || []);
    } catch (err) {
      console.error('Failed to fetch forum data:', err);
      setForumPosts([]);
      setForumParticipants([]);
    } finally {
      setForumLoading(false);
    }
  };

  const handleForumDM = (participant) => {
    if (!participant.allow_dms) {
      toast.error('This traveler has disabled direct messages');
      return;
    }
    setSelectedTraveler({ 
      id: participant.user_id, 
      name: participant.name 
    });
    setShowMessageModal(true);
  };

  const handleMatchRequest = (travelerId, action) => {
    if (action === 'request') {
      setMatchRequests([...matchRequests, travelerId]);
      toast.success('Match request sent! They\'ll be notified.');
    }
    setSoloTravelers(prev => prev.map(t => 
      t.id === travelerId ? { ...t, matchStatus: action === 'request' ? 'requested' : 'declined' } : t
    ));
  };

  const handlePostSubmit = async (e) => {
    e.preventDefault();
    if (!newPost.trim() || !selectedForumTrip) return;
    
    try {
      const res = await axios.post(
        `${API}/forum/trips/${selectedForumTrip.trip_id}/${encodeURIComponent(selectedForumTrip.tour_date)}/posts`,
        {
          trip_id: selectedForumTrip.trip_id,
          tour_date: selectedForumTrip.tour_date,
          content: newPost,
          category: postCategory
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setForumPosts([res.data, ...forumPosts]);
      setNewPost('');
      setPostCategory('general');
      toast.success('Post shared with your trip group!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create post');
    }
  };

  const handleLikePost = async (postId) => {
    try {
      const res = await axios.post(
        `${API}/forum/posts/${postId}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setForumPosts(posts => posts.map(p => 
        p.id === postId 
          ? { ...p, likes: res.data.action === 'liked' ? [...(p.likes || []), user.id] : (p.likes || []).filter(id => id !== user.id) }
          : p
      ));
    } catch (err) {
      toast.error('Failed to like post');
    }
  };

  const handleExpandPost = async (postId) => {
    if (expandedPost === postId) {
      setExpandedPost(null);
      return;
    }
    
    setExpandedPost(postId);
    
    if (!postReplies[postId]) {
      try {
        const res = await axios.get(`${API}/forum/posts/${postId}/replies`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPostReplies(prev => ({ ...prev, [postId]: res.data }));
      } catch (err) {
        console.error('Failed to fetch replies:', err);
      }
    }
  };

  const handleReplySubmit = async (postId) => {
    if (!replyText.trim()) return;
    
    try {
      const res = await axios.post(
        `${API}/forum/posts/${postId}/replies`,
        { content: replyText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPostReplies(prev => ({
        ...prev,
        [postId]: [...(prev[postId] || []), res.data]
      }));
      
      setForumPosts(posts => posts.map(p => 
        p.id === postId ? { ...p, reply_count: (p.reply_count || 0) + 1 } : p
      ));
      
      setReplyText('');
      toast.success('Reply posted!');
    } catch (err) {
      toast.error('Failed to post reply');
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await axios.delete(`${API}/forum/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setForumPosts(posts => posts.filter(p => p.id !== postId));
      toast.success('Post deleted');
    } catch (err) {
      console.error('Delete error:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete post');
    }
  };

  const handleSaveProfile = async () => {
    try {
      toast.success('Profile updated successfully!');
      setEditingProfile(false);
    } catch (err) {
      toast.error('Failed to update profile');
    }
  };

  const handleSaveInterest = async () => {
    if (!tripInterest.tripId) {
      toast.error('Please select a trip you\'re interested in');
      return;
    }
    try {
      const selectedTrip = trips.find(t => t.id === tripInterest.tripId);
      await axios.post(`${API}/users/travel-interest`, {
        trip_id: tripInterest.tripId,
        trip_title: selectedTrip?.title || 'Unknown Trip',
        preferred_dates: tripInterest.preferredDates,
        looking_for_roommate: tripInterest.lookingForRoommate,
        looking_for_partner: tripInterest.lookingForTravelPartner,
        bio: tripInterest.bio
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Your travel interest has been saved! Other solo travelers can now see you\'re looking for connections.');
    } catch (err) {
      toast.error('Failed to save interest');
    }
  };

  const handleOpenMessage = (traveler) => {
    setSelectedTraveler(traveler);
    setMessageText('');
    setShowMessageModal(true);
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedTraveler) return;
    
    try {
      await axios.post(`${API}/messages`, {
        recipient_id: selectedTraveler.id,
        subject: messageSubject.trim() || null,
        content: messageText
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(`Message sent to ${selectedTraveler.name}!`);
      setShowMessageModal(false);
      setMessageText('');
      setMessageSubject('');
      setSelectedTraveler(null);
      
      const res = await axios.get(`${API}/messages/conversations`, { headers: { Authorization: `Bearer ${token}` } });
      setConversations(res.data || []);
    } catch (err) {
      toast.error('Failed to send message');
    }
  };

  const handleOpenConversation = async (conversation) => {
    setActiveConversation(conversation);
    setShowInbox(true);
    
    try {
      const res = await axios.get(`${API}/messages/${conversation.partner_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversationMessages(res.data || []);
      
      const statsRes = await axios.get(`${API}/users/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(statsRes.data || {});
    } catch (err) {
      console.error('Failed to load messages:', err);
    }
  };

  const handleSendInboxMessage = async () => {
    if (!messageText.trim() || !activeConversation) return;
    
    try {
      await axios.post(`${API}/messages`, {
        recipient_id: activeConversation.partner_id,
        content: messageText
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      const res = await axios.get(`${API}/messages/${activeConversation.partner_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversationMessages(res.data || []);
      setMessageText('');
      
      const convRes = await axios.get(`${API}/messages/conversations`, { headers: { Authorization: `Bearer ${token}` } });
      setConversations(convRes.data || []);
    } catch (err) {
      toast.error('Failed to send message');
    }
  };

  // ==================== EFFECTS ====================
  
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
    setProfile(prev => ({
      ...prev,
      fullName: user.full_name || '',
      email: user.email || ''
    }));
    
    const urlParams = new URLSearchParams(window.location.search);
    const addonSuccess = urlParams.get('addon_success');
    const addonCancelled = urlParams.get('addon_cancelled');
    
    if (addonSuccess === 'true') {
      toast.success('Excursions added to your booking successfully!');
      window.history.replaceState({}, '', '/member');
    } else if (addonCancelled === 'true') {
      toast.info('Add-on purchase was cancelled');
      window.history.replaceState({}, '', '/member');
    }
  }, [user, authLoading]);

  useEffect(() => {
    if (selectedForumTrip) {
      fetchForumPosts(selectedForumTrip.trip_id, selectedForumTrip.tour_date);
    }
  }, [selectedForumTrip]);

  // ==================== RENDER ====================
  
  if (!user) return null;

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-muted/30">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-primary mb-2">
            Welcome back, {user.full_name?.split(' ')[0] || 'Traveler'}! 👋
          </h1>
          <p className="text-muted-foreground">
            Your travel hub for managing trips, connecting with travelers, and exploring new adventures.
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white shadow-sm flex-wrap h-auto p-1">
            <TabsTrigger value="home" className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              Home
            </TabsTrigger>
            <TabsTrigger value="trips" className="flex items-center gap-2">
              <Plane className="h-4 w-4" />
              My Trips
            </TabsTrigger>
            <TabsTrigger value="matching" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Find Partners
            </TabsTrigger>
            <TabsTrigger value="profile" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Profile
            </TabsTrigger>
          </TabsList>

          <TabsContent value="home">
            <DashboardHomeTab
              user={user}
              stats={stats}
              bookings={bookings}
              trips={trips}
              conversations={conversations}
              tripInterest={tripInterest}
              setTripInterest={setTripInterest}
              handleSaveInterest={handleSaveInterest}
              setActiveTab={setActiveTab}
            />
          </TabsContent>

          <TabsContent value="trips">
            <DashboardTripsTab
              bookings={bookings}
              handleOpenAddOns={handleOpenAddOns}
              setSelectedForumTrip={setSelectedForumTrip}
              setActiveTab={setActiveTab}
            />
          </TabsContent>

          <TabsContent value="matching">
            <DashboardMatchingTab
              user={user}
              stats={stats}
              conversations={conversations}
              soloTravelers={soloTravelers}
              handleOpenConversation={handleOpenConversation}
              handleOpenMessage={handleOpenMessage}
              handleMatchRequest={handleMatchRequest}
            />
          </TabsContent>

          <TabsContent value="forum">
            <DashboardForumTab
              user={user}
              forumTrips={forumTrips}
              selectedForumTrip={selectedForumTrip}
              setSelectedForumTrip={setSelectedForumTrip}
              forumTravelerCount={forumTravelerCount}
              forumParticipants={forumParticipants}
              showParticipants={showParticipants}
              setShowParticipants={setShowParticipants}
              forumPosts={forumPosts}
              forumLoading={forumLoading}
              newPost={newPost}
              setNewPost={setNewPost}
              postCategory={postCategory}
              setPostCategory={setPostCategory}
              expandedPost={expandedPost}
              postReplies={postReplies}
              replyText={replyText}
              setReplyText={setReplyText}
              handlePostSubmit={handlePostSubmit}
              handleLikePost={handleLikePost}
              handleExpandPost={handleExpandPost}
              handleReplySubmit={handleReplySubmit}
              handleDeletePost={handleDeletePost}
              handleForumDM={handleForumDM}
            />
          </TabsContent>

          <TabsContent value="profile">
            <DashboardProfileTab
              editingProfile={editingProfile}
              setEditingProfile={setEditingProfile}
              profile={profile}
              setProfile={setProfile}
              commsPrefs={commsPrefs}
              setCommsPrefs={setCommsPrefs}
              handleSaveProfile={handleSaveProfile}
              handleSaveCommsPrefs={handleSaveCommsPrefs}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Modals */}
      <MessageModal
        show={showMessageModal}
        selectedTraveler={selectedTraveler}
        messageSubject={messageSubject}
        setMessageSubject={setMessageSubject}
        messageText={messageText}
        setMessageText={setMessageText}
        onClose={() => {
          setShowMessageModal(false);
          setMessageSubject('');
          setMessageText('');
        }}
        onSend={handleSendMessage}
      />

      <InboxModal
        show={showInbox}
        user={user}
        activeConversation={activeConversation}
        conversationMessages={conversationMessages}
        messageText={messageText}
        setMessageText={setMessageText}
        onClose={() => {
          setShowInbox(false);
          setActiveConversation(null);
          setConversationMessages([]);
        }}
        onSend={handleSendInboxMessage}
      />

      <AddOnsModal
        show={showAddOnsModal}
        selectedBooking={selectedBookingForAddOns}
        loadingAddOns={loadingAddOns}
        modalAvailableAddOns={modalAvailableAddOns}
        modalSelectedAddOns={modalSelectedAddOns}
        toggleAddOnSelection={toggleAddOnSelection}
        onClose={() => setShowAddOnsModal(false)}
        onConfirm={handleAddAddOnsToBooking}
        addingAddOns={addingAddOns}
        processingPayment={processingPayment}
      />

      <PaymentProcessingOverlay show={processingPayment} />
    </div>
  );
}
